import React, { useMemo, useState } from "react";
import useSeo from "../seo/useSeo";
import useJsonLd from "../seo/useJsonLd";
import ActionCard from "../components/ActionCard";
import MergePdf from "../components/MergePdf";
import ImagesToPdf from "../components/ImagesToPdf";

export default function FusionPage() {
  const [mode, setMode] = useState(null); // null | "pdf" | "jpg"

  /* ================= SEO ================= */
  useSeo({
    title: "Fusion PDF – Fusionner des PDF ou images en PDF | pdf-convert.fr",
    description:
      "Fusionnez plusieurs PDF en un seul document ou regroupez des images JPG en PDF A4. Gratuit, rapide et 100% local, sans envoi de fichiers.",
    canonical: "https://pdf-convert.fr/fusion",
  });

  /* ================= BREADCRUMB ================= */
  const breadcrumbJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Accueil",
          item: "https://pdf-convert.fr/home",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Fusion",
          item: "https://pdf-convert.fr/fusion",
        },
      ],
    }),
    []
  );
  useJsonLd("breadcrumb-fusion", breadcrumbJsonLd);

  /* ================= FAQ ================= */
  const faqItems = useMemo(
    () => [
      {
        q: "Comment fusionner plusieurs PDF en un seul fichier ?",
        a: "Sélectionnez au moins deux fichiers PDF, ajustez l’ordre si besoin, puis cliquez sur “Fusionner”. Le document final est généré instantanément.",
      },
      {
        q: "Puis-je regrouper plusieurs images JPG en un PDF ?",
        a: "Oui. Sélectionnez plusieurs images et générez un PDF au format A4. Les images sont ajustées pour remplir la page au maximum sans déformation.",
      },
      {
        q: "Mes fichiers sont-ils envoyés sur un serveur ?",
        a: "Non. Les opérations sont effectuées localement dans votre navigateur. Aucun fichier n’est envoyé ni stocké.",
      },
      {
        q: "Pourquoi certains PDF ne se fusionnent pas ?",
        a: "Certains PDF peuvent être corrompus ou protégés par mot de passe. Dans ce cas, la fusion peut échouer.",
      },
    ],
    []
  );

  const faqJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    }),
    [faqItems]
  );
  useJsonLd("faq-jsonld-fusion", faqJsonLd);

  return (
    <main style={{ maxWidth: 900, margin: "0 auto", padding: 16 }}>
      <h1>Fusion</h1>

      {/* ===== TEXTE SEO VISIBLE (important Google/AdSense) ===== */}
      <p
        style={{
          textAlign: "center",
          color: "#666",
          maxWidth: 760,
          margin: "12px auto 22px",
          lineHeight: 1.5,
        }}
      >
        La fusion de fichiers permet de regrouper plusieurs documents en un seul
        PDF. Sur <strong>pdf-convert.fr</strong>, vous pouvez fusionner
        plusieurs fichiers PDF entre eux, ou regrouper plusieurs images JPG en
        un PDF A4. Tout se fait <strong>dans votre navigateur</strong>, sans
        envoi de fichiers.
      </p>

      {/* ===== CHOIX DE L’OPÉRATION (cartes comme Conversion) ===== */}
      {!mode && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
            marginTop: 18,
          }}
        >
          <ActionCard
            title="Fusion PDF"
            description="Regrouper plusieurs fichiers PDF en un seul document"
            onClick={() => setMode("pdf")}
          />

          <ActionCard
            title="JPG → PDF"
            description="Fusionner plusieurs images JPG en un PDF A4"
            onClick={() => setMode("jpg")}
          />
        </div>
      )}

      {/* ===== CONTENU ===== */}
      {mode && (
        <div style={{ marginTop: 18 }}>
          <button onClick={() => setMode(null)} style={{ marginBottom: 14 }}>
            ← Changer d’opération
          </button>

          {mode === "pdf" && (
            <>
              <h2 style={{ fontSize: 18, marginTop: 0 }}>
                Fusionner plusieurs PDF
              </h2>
              <p style={{ color: "#666", marginTop: 6 }}>
                Sélectionne au moins 2 fichiers PDF, ajuste l’ordre, puis génère
                ton PDF final.
              </p>
              <MergePdf />
            </>
          )}

          {mode === "jpg" && (
            <>
              <h2 style={{ fontSize: 18, marginTop: 0 }}>
                Fusionner plusieurs JPG en PDF
              </h2>
              <p style={{ color: "#666", marginTop: 6 }}>
                Sélectionne tes images, elles seront regroupées dans un PDF A4.
              </p>
              <ImagesToPdf />
            </>
          )}
        </div>
      )}

      {/* ===== LIENS UTILES (SEO + UX) ===== */}
      <div style={{ marginTop: 22, textAlign: "center", color: "#666" }}>
        <p style={{ margin: 0 }}>
          Pour d’autres outils, consulte la{" "}
          <a href="/conversion" style={{ textDecoration: "underline" }}>
            page Conversion
          </a>{" "}
          ou la{" "}
          <a href="/reduction" style={{ textDecoration: "underline" }}>
            réduction d’image
          </a>
          .
        </p>
      </div>
    </main>
  );
}
